/**
 * Partner Quote Section
 *
 * Complete section for fetching, comparing, and selecting partner quotes
 * Integrates into Design Studio workflow
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Stack,
  Paper,
} from '@mui/material';
import {
  RequestQuote as QuoteIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import PartnerQuoteWidget from './PartnerQuoteWidget';
import { saveQuotesToLook } from '../../api/partnerQuotes';

/**
 * Partner Quote Section Component
 *
 * @param {Object} props
 * @param {string} props.brandId - Brand ID
 * @param {string} props.lookId - Look configuration ID
 * @param {string} props.garmentSKU - Garment SKU (e.g., "GILDAN-5000")
 * @param {number} props.quantity - Default quantity for quote
 * @param {Object} props.decorationSpecs - Decoration specifications
 * @param {Object} props.garmentDetails - Garment details (type, color, sizes)
 * @param {Array} props.existingQuotes - Existing saved quotes
 * @param {Object} props.selectedQuote - Currently selected quote
 * @param {Function} props.onQuotesSaved - Callback when quotes are saved
 */
const PartnerQuoteSection = ({
  brandId,
  lookId,
  garmentSKU,
  quantity = 50,
  decorationSpecs = {
    method: 'dtg',
    placements: [{
      location: 'front_chest',
      width: 12,
      height: 12
    }]
  },
  garmentDetails = {
    type: 'tshirt',
    color: 'white',
    sizes: ['S', 'M', 'L', 'XL']
  },
  existingQuotes = [],
  selectedQuote = null,
  onQuotesSaved,
}) => {
  const [showWidget, setShowWidget] = useState(false);
  const [savedQuotes, setSavedQuotes] = useState(existingQuotes);
  const [selected, setSelected] = useState(selectedQuote);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSavedQuotes(existingQuotes);
    setSelected(selectedQuote);
  }, [existingQuotes, selectedQuote]);

  const handleQuoteSelected = async (quote) => {
    setSelected(quote);
  };

  const handleSaveQuotes = async (quotes, selectedQuote) => {
    if (!brandId || !lookId) {
      toast.error('Missing brand or look ID');
      return;
    }

    setSaving(true);
    try {
      await saveQuotesToLook(brandId, lookId, quotes, selectedQuote.partnerId);
      setSavedQuotes(quotes);
      setSelected(selectedQuote);
      toast.success('Partner quotes saved successfully');

      if (onQuotesSaved) {
        onQuotesSaved(quotes, selectedQuote);
      }
    } catch (error) {
      console.error('Error saving quotes:', error);
      toast.error('Failed to save quotes');
    } finally {
      setSaving(false);
    }
  };

  const hasSavedQuotes = savedQuotes && savedQuotes.length > 0;
  const hasRequiredData = garmentSKU && quantity && decorationSpecs;

  return (
    <Box>
      <Card>
        <CardContent>
          <Stack spacing={3}>
            {/* Header */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Production Partner Quotes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Get real-time cost estimates from production partners including decoration,
                setup fees, shipping, and total cost per unit.
              </Typography>
            </Box>

            {/* Saved Quote Display */}
            {hasSavedQuotes && selected && (
              <Paper sx={{ p: 2, bgcolor: 'success.50', border: 1, borderColor: 'success.200' }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <CheckIcon color="success" />
                  <Box flex={1}>
                    <Typography variant="subtitle2" color="success.dark">
                      Selected Partner: {selected.partnerName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Cost: ${selected.quote?.totalCost?.toFixed(2)}/unit •
                      Lead Time: {selected.quote?.leadTimeDays} days
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    onClick={() => setShowWidget(true)}
                    variant="outlined"
                  >
                    Update Quotes
                  </Button>
                </Stack>
              </Paper>
            )}

            {/* Get Quotes Button */}
            {!showWidget && !hasSavedQuotes && (
              <Box>
                {!hasRequiredData && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Complete garment details (SKU, quantity, decoration specs) to get partner quotes.
                  </Alert>
                )}
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<QuoteIcon />}
                  onClick={() => setShowWidget(true)}
                  disabled={!hasRequiredData}
                  fullWidth
                >
                  Get Partner Quotes
                </Button>
              </Box>
            )}

            {/* Partner Quote Widget */}
            {showWidget && hasRequiredData && (
              <PartnerQuoteWidget
                garmentSKU={garmentSKU}
                quantity={quantity}
                decorationSpecs={decorationSpecs}
                garmentDetails={garmentDetails}
                initialQuotes={savedQuotes}
                autoFetch={!hasSavedQuotes}
                onQuoteSelected={handleQuoteSelected}
                onSave={handleSaveQuotes}
                saving={saving}
              />
            )}

            {/* Current Data Summary */}
            {hasRequiredData && (
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Quote Request Details
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    <strong>Garment:</strong> {garmentSKU} ({garmentDetails.type})
                  </Typography>
                  <Typography variant="body2">
                    <strong>Quantity:</strong> {quantity} units
                  </Typography>
                  <Typography variant="body2">
                    <strong>Decoration:</strong> {decorationSpecs.method.toUpperCase()}
                  </Typography>
                  {decorationSpecs.placements && (
                    <Typography variant="body2">
                      <strong>Placements:</strong> {decorationSpecs.placements.length} location(s)
                    </Typography>
                  )}
                </Stack>
              </Paper>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PartnerQuoteSection;
